import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { Block, TeamBlockContent } from '@/types';
import { uboTheme, themeColors } from '@/theme/uboTheme';
import { resolveTeamMember, resolveTeamMemberId, useTeamMembers } from '@/store/teamMemberStore';
import PageHeader from './PageHeader';
import {
  useQuoteClientLogo,
  useQuotePageBrand,
  useResolvedAgencyLogoUrl,
} from '@/store/quoteClientLogoContext';

interface TeamBlockProps {
  block: Block;
}

const MAX_VISIBLE_TEAM_MEMBERS = 8;
const GRID_GAP_PX = 20;
const ROW_GAP_PX = 28;
const LABEL_HEIGHT_PX = 64;
const TITLE_FONT_PX = 56;
const TITLE_MIN_FONT_PX = 20;

/**
 * Naast de foto's is de titelkolom smal: de kop krimpt tot het langste woord past,
 * in plaats van midden in een woord af te breken ("IMPLEM / ENTATI / ON").
 */
function useFittedTitleFontSize(
  ref: RefObject<HTMLHeadingElement>,
  title: string,
  enabled: boolean,
): number {
  const [fontSize, setFontSize] = useState(TITLE_FONT_PX);

  useLayoutEffect(() => {
    const heading = ref.current;
    if (!heading || !enabled) {
      setFontSize(TITLE_FONT_PX);
      return;
    }
    let cancelled = false;
    const fit = () => {
      const column = heading.parentElement;
      if (cancelled || !column) return;
      // min-content = breedte van het langste woord. Subpixels tellen: scrollWidth
      // rondt af en mist een woord dat 0,4px te breed is. De verhouding tussen beide
      // rechthoeken is ongevoelig voor de schaal-transform van de kaart.
      heading.style.overflowWrap = 'normal';
      heading.style.width = 'min-content';
      const available = column.getBoundingClientRect().width;
      let size = TITLE_FONT_PX;
      heading.style.fontSize = `${size}px`;
      while (size > TITLE_MIN_FONT_PX && heading.getBoundingClientRect().width > available) {
        size -= 1;
        heading.style.fontSize = `${size}px`;
      }
      heading.style.width = '';
      heading.style.overflowWrap = 'break-word';
      setFontSize(size);
    };
    fit();
    // Het kopfont kan later laden en is breder dan de fallback.
    void document.fonts?.ready.then(fit);
    return () => {
      cancelled = true;
    };
  }, [ref, title, enabled]);

  return fontSize;
}

/**
 * Vijf en zes leden staan in twee rijen van drie; zeven en acht in rijen van vier.
 * Een dubbele kolomverdeling centreert ook een onvolledige laatste rij precies.
 */
function getLayout(count: number): { cols: number; cardWidth: string; width: string } {
  const cols = count <= 4 ? Math.max(count, 1) : count <= 6 ? 3 : 4;
  const rows = Math.max(1, Math.ceil(count / cols));
  const cardWidth = `min(320px, calc((100cqw - ${(cols - 1) * GRID_GAP_PX}px) / ${cols}), calc(((100cqh - ${(rows - 1) * ROW_GAP_PX}px) / ${rows} - ${LABEL_HEIGHT_PX}px) * 2 / 3))`;
  return { cols, cardWidth, width: `calc(${cols} * ${cardWidth} + ${(cols - 1) * GRID_GAP_PX}px)` };
}

function TeamBlock({ block }: TeamBlockProps) {
  const content = block.content as TeamBlockContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const effectiveTheme = content.theme ?? quoteBrand;
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const { byId: catalog } = useTeamMembers();

  const colors = themeColors(effectiveTheme);
  const selectedMembers = content.selectedMembers || [];
  const teamPageBg = effectiveTheme === 'ubx' ? colors.primary : '#070744';
  const roleOverrides = content.roleOverrides || {};
  const visibleMembers = selectedMembers.slice(0, MAX_VISIBLE_TEAM_MEMBERS).flatMap((id) => {
    const member = resolveTeamMember(id, content.members, catalog);
    return member ? [{ id, member }] : [];
  });
  const visibleCount = visibleMembers.length;
  const { cols, cardWidth, width } = getLayout(visibleCount);
  const titleAbovePhotos = visibleCount < 5;
  const title = content.title ?? 'Het team';
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleFontSize = useFittedTitleFontSize(titleRef, title, !titleAbovePhotos);

  return (
    <div
      className="w-full h-full flex-1 relative overflow-hidden flex flex-col text-white"
      style={{
        backgroundColor: teamPageBg,
        padding: 50,
      }}
    >
      <PageHeader
        clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
        agencyLogoUrl={agencyUrl}
        brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
        variant="dark"
      />

      <div
        className="flex flex-1 min-h-0"
        style={{
          marginTop: 32,
          gap: titleAbovePhotos ? 24 : 40,
          flexDirection: titleAbovePhotos ? 'column' : 'row',
        }}
      >
        <div
          className="shrink-0 flex flex-col justify-center"
          style={{
            width: titleAbovePhotos ? undefined : 210,
            paddingBottom: titleAbovePhotos ? 0 : 24,
          }}
        >
          <h2
            ref={titleRef}
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primaryFallback,
              overflowWrap: 'break-word',
            }}
          >
            {title}
          </h2>
        </div>

        {visibleCount > 0 && (
          <div className="flex-1 min-h-0 min-w-0" style={{ containerType: 'size' }}>
            <div className="h-full flex items-center justify-center">
              <div
                className="grid"
                style={{
                  width: titleAbovePhotos ? '100%' : width,
                  columnGap: GRID_GAP_PX,
                  rowGap: ROW_GAP_PX,
                  gridTemplateColumns: `repeat(${cols * 2}, minmax(0, 1fr))`,
                }}
              >
                {visibleMembers.map(({ id, member }, index) => {
                  const row = Math.floor(index / cols);
                  const membersInRow = Math.min(cols, visibleCount - row * cols);
                  const column = (index % cols) * 2 + (cols - membersInRow) + 1;
                  return (
                    <figure
                      key={id}
                      className="m-0 min-w-0 min-h-0 flex flex-col"
                      style={{
                        gridColumn: `${column} / span 2`,
                        gridRow: row + 1,
                        width: titleAbovePhotos ? cardWidth : undefined,
                        justifySelf: titleAbovePhotos ? 'center' : undefined,
                      }}
                    >
                      <div
                        className="relative w-full overflow-hidden"
                        style={{
                          aspectRatio: '2 / 3',
                          backgroundColor: '#060b1f',
                        }}
                      >
                        <img
                          src={member.img}
                          alt={member.name}
                          draggable={false}
                          onMouseDown={(event) => {
                            // Het portret is onderdeel van de teampagina, geen losse editor-afbeelding.
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          className="absolute inset-0 h-full w-full object-contain object-center"
                          loading="eager"
                          decoding="async"
                        />
                      </div>
                      <figcaption
                        className="shrink-0 pt-3 text-left"
                        style={{
                          minHeight: LABEL_HEIGHT_PX,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        <p className="m-0 text-base font-bold uppercase leading-tight tracking-wide">
                          {member.firstName}
                        </p>
                        <span className="mt-1 block text-[11px] leading-tight text-white/65">
                          {(roleOverrides[id] ?? roleOverrides[resolveTeamMemberId(id)]) ||
                            member.title}
                        </span>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {selectedMembers.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
            Klik om teamleden te selecteren
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamBlock;
